/**
 * miniTreeRows.js — story 167 (HU-03, HU-06 · RN-05, RN-08.8, D1, D16). Pure.
 * The rows of the mini tree (Tree's own projection, simplified), the SUB/OP tag of each row, and what a
 * drag over a row means: behind X, or why not.
 */
import { PROCESS_TREE_ROW_KIND, buildProcessTreeRows, findContainerNode } from './processTree'

export const MINI_TREE_WARNING_KIND = 'SUBPROCESS_WARNING'

export const MINI_TREE_TAG = { OP: 'OP', SUB: 'SUB' }

export const DROP_TARGET_KIND = { NONE: 'NONE', AFTER: 'AFTER', CANVAS: 'CANVAS', INVALID: 'INVALID' }

export const NO_DROP_TARGET = { kind: DROP_TARGET_KIND.NONE, rowKey: '', nodeId: '', label: '', reasonKey: '', reasonParams: {} }
export const CANVAS_DROP_TARGET = { ...NO_DROP_TARGET, kind: DROP_TARGET_KIND.CANVAS }

const DROP_ON_OPERATION = 'Drop it on an operation or on the canvas'
export const EMPTY_SPACE_DROP_TARGET = { ...NO_DROP_TARGET, kind: DROP_TARGET_KIND.INVALID, reasonKey: DROP_ON_OPERATION }

const NODE_ROW_KINDS = [PROCESS_TREE_ROW_KIND.JOB, PROCESS_TREE_ROW_KIND.SET, PROCESS_TREE_ROW_KIND.REFERENCE]

const warningRowOf = (setRow, message) => ({
  ...setRow,
  kind: MINI_TREE_WARNING_KIND,
  key: setRow.key + '/warning',
  parentKey: setRow.key,
  contentOf: setRow.key,
  depth: setRow.depth + 1,
  nodeId: '',
  collapsible: false,
  failedHydration: false,
  message
})

/**
 * RN-05.1/RN-05.2: Tree's rows without the parallel headers, plus one warning row under every SUB whose
 * content could not be loaded (D16.5), carrying the server's message.
 */
export const buildMiniTreeRows = ({ workflow, coverage = null, failedSetNodeIds = [], failedSetMessages = {}, expandedMaterialKeys = new Set() }) => {
  const { rows } = buildProcessTreeRows({ workflow, coverage, failedSetNodeIds, detailedBranches: false, expandedMaterialKeys })
  return rows.flatMap((row) => (row.kind === PROCESS_TREE_ROW_KIND.SET && row.failedHydration
    ? [row, warningRowOf(row, failedSetMessages[row.nodeId] || '')]
    : [row]))
}

/** nodeId → nodeType at every loaded level, so a reference row can wear the tag of the node it points at. */
export const collectNodeTypes = (nodes) => {
  const types = {}
  const visit = (level) => {
    (level || []).forEach((node) => {
      types[node.id] = node.nodeType
      visit(node.subWorkflow?.nodes)
    })
  }
  visit(nodes)
  return types
}

/** D1/RN-05.3: SUB for a subworkflow, OP for an operation, nothing for material or warning rows. */
export const miniTreeTagOf = (row, nodeTypeById) => {
  if (row.kind === PROCESS_TREE_ROW_KIND.SET) {
    return MINI_TREE_TAG.SUB
  }
  if (row.kind === PROCESS_TREE_ROW_KIND.JOB) {
    return MINI_TREE_TAG.OP
  }
  if (row.kind === PROCESS_TREE_ROW_KIND.REFERENCE) {
    return nodeTypeById[row.nodeId] === 'WORKFLOW' ? MINI_TREE_TAG.SUB : MINI_TREE_TAG.OP
  }
  return ''
}

/**
 * RN-08.8/RN-08.12: a row admits a drop only when it is an operation or a subworkflow — or a reference to one —
 * of the FIRST level of the open workflow. Whether the operation has parts plays no part (D14).
 */
export const resolveRowDropTarget = (row, workflowNodes, effectiveNodes) => {
  const base = { ...NO_DROP_TARGET, rowKey: row.key }
  if (row.kind === MINI_TREE_WARNING_KIND) {
    return row.message
      ? { ...base, kind: DROP_TARGET_KIND.INVALID, reasonKey: 'This subprocess could not be loaded: {{message}}', reasonParams: { message: row.message } }
      : { ...base, kind: DROP_TARGET_KIND.INVALID, reasonKey: 'This subprocess could not be loaded' }
  }
  if (!NODE_ROW_KINDS.includes(row.kind)) {
    return { ...base, kind: DROP_TARGET_KIND.INVALID, reasonKey: DROP_ON_OPERATION }
  }
  const node = (workflowNodes || []).find((candidate) => candidate.id === row.nodeId)
  if (!node) {
    return {
      ...base,
      kind: DROP_TARGET_KIND.INVALID,
      reasonKey: 'This belongs to the subworkflow «{{code}}» and cannot be changed here',
      reasonParams: { code: findContainerNode(row.nodeId, effectiveNodes)?.code || '' }
    }
  }
  return { ...base, kind: DROP_TARGET_KIND.AFTER, nodeId: node.id, label: node.name || node.code || '' }
}

export const NO_DESTINATION = { key: '', params: {} }

/** RN-07.4, D16.4: what the panel adds to the effect while dragging. */
export const destinationOf = (target) => {
  if (target.kind === DROP_TARGET_KIND.AFTER) {
    return { key: 'Inserted after «{{name}}»', params: { name: target.label } }
  }
  if (target.kind === DROP_TARGET_KIND.CANVAS) {
    return { key: 'Dropped loose on the canvas', params: {} }
  }
  if (target.kind === DROP_TARGET_KIND.INVALID) {
    return { key: target.reasonKey, params: target.reasonParams }
  }
  return NO_DESTINATION
}
